import React from 'react';
import { IonicButton, IonicInput } from 'ionic-ui';
import SettingsGridWrapper from '../../../../../components/Dashboard/settings/ui/SettingsGridWrapper';
import SettingsSectionHeader from '../../../../../components/Dashboard/settings/ui/SettingsSectionHeader';
import SettingsMainContentWrapper from '../../../../../components/Dashboard/settings/ui/SettingsMainContentWrapper';
import { IonIcon, useIonViewWillEnter } from '@ionic/react';
import { lockClosed } from 'ionicons/icons';
import PasswordInput from '../../../../../components/ui/PasswordInput';
import PasswordInputWithStrengthLevel from '../../../../../components/Dashboard/settings/ui/PasswordInputWithStrengthLevel';
import SettingsActionButtons from '../../../../../components/Dashboard/settings/ui/SettingsActionButtons';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { passwordResetSchema } from '../../../../../schemas/validation';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import z from 'zod';
import usePasswordResetService from '../../../../../services/changeReset/hooks/usePasswordResetService';
import useSignOut from '../../../../../services/authentication/hooks/useSignOut';

const FormSchema = z.object({
  ...passwordResetSchema.shape,
  current_password: z
    .string()
    .min(1, { message: 'your current password is required' }),
});

export default function SecurityAndPrivacy() {
  const userProfile = useSelector((state: RootState) => state.userProfile);
  const formMethods = useForm({
    resolver: zodResolver(FormSchema),
  });
  const { requestSignOut } = useSignOut();

  const {
    requestPasswordResetPermit,
    submitNewPassword,
    tokenTimer,
    canChangePass,
    tokenIsValid,
    cancelRequest,
  } = usePasswordResetService();

  const sendPermitRequest = () => {
    if (!userProfile?.user?.email) return;
    requestPasswordResetPermit(userProfile?.user.email);
  };

  const handlePasswordReset = formMethods.handleSubmit((formEntry) => {
    if (!userProfile?.user?.email) return requestSignOut();
    const { current_password: _, ...res } = formEntry;
    submitNewPassword({
      email: userProfile?.user.email,
      ...res,
    });
  });

  useIonViewWillEnter(() => {
    formMethods.reset();
  });

  const handleFormReset = () => {
    cancelRequest();
    formMethods.reset();
  };

  return (
    <SettingsGridWrapper>
      <SettingsSectionHeader
        header="security settings"
        helpTips="this may help you go"
        subheader="Manage your account preferences and security settings."
      />
      <SettingsMainContentWrapper>
        <div className="mb-4">
          <IonicButton
            variant="outline"
            disabled={tokenIsValid}
            className="w-full"
            onClick={sendPermitRequest}
          >
            {canChangePass
              ? `Resend Token ${tokenIsValid ? `in ${tokenTimer}` : ''}`
              : 'Request Token'}
          </IonicButton>
        </div>
        <div>
          <form className="space-y-4 w-full">
            <div className="sm:w-1/2">
              <Controller
                control={formMethods.control}
                name="token"
                render={({ field, fieldState }) => (
                  <IonicInput
                    label="Reset token"
                    type="text"
                    autoComplete="off"
                    aria-label="password reset token"
                    placeholder="xxxxxx"
                    maxLength={6}
                    hint={fieldState.error?.message}
                    hintClass="text-red-600 dark:text-red-600"
                    {...field}
                    value={field.value || ''}
                  />
                )}
              />
            </div>
            <Controller
              control={formMethods.control}
              name="current_password"
              render={({ field, fieldState }) => (
                <PasswordInput
                  {...field}
                  label="Current Password"
                  aria-label="current password"
                  placeholder="Enter current password"
                  hint={fieldState.error?.message}
                  hintClass="text-red-600 dark:text-red-600"
                  prefix={<IonIcon icon={lockClosed} />}
                />
              )}
            />
            <Controller
              control={formMethods.control}
              name="password"
              render={({ field, fieldState }) => (
                <PasswordInputWithStrengthLevel
                  {...field}
                  label="New Password"
                  aria-label="new password"
                  hint={fieldState.error?.message}
                  hintClass="text-red-600 dark:text-red-600"
                  prefix={<IonIcon icon={lockClosed} />}
                  placeholder="Enter new password"
                />
              )}
            />
            <Controller
              control={formMethods.control}
              name="confirm_password"
              render={({ field, fieldState }) => (
                <PasswordInput
                  {...field}
                  label="Confirm Password"
                  aria-label="confirm password"
                  placeholder="Re-enter new password"
                  hint={fieldState.error?.message}
                  hintClass="text-red-600 dark:text-red-600"
                  prefix={<IonIcon icon={lockClosed} />}
                />
              )}
            />
            {/* <div>
              <label
                htmlFor="store-status"
                className="high-emphasis-text text-sm"
              >
                SMS Authentication
              </label>
              <CustomToggle
                label="Allow"
                subLabel={`Get codes via SMS to ${'+234***5678'}`}
              />
            </div> */}

            {/* <div className="p-2 space-y-2">
              <CustomToggle
                label="Authenticator App"
                subLabel="Use Google Authenticator or similar"
              />
              <div className="w-full h-[1px] rounded-sm bg-border"></div>

              <IonicButton variant="outline" className="button-with-icon">
                <IonIcon size="24" icon={shieldCheckmarkOutline} />
                <span>Setup Authentication</span>
              </IonicButton>
            </div> */}

            {/* SECURITY SECTION */}
            {/* <div className="space-y-4">
              <label
                htmlFor="store-status"
                className="high-emphasis-text text-sm"
                >
                Security Question
              </label>
              <div className="space-y-4 p-2 border border-border rounded-md">
                <IonicInput
                  label="Question"
                  placeholder="What is your Mothers Maiden name"
                  type="select"
                />
                <div className="w-full h-[1px] rounded-sm bg-border"></div>
                <IonicInput label="Answer" placeholder="XXXXXXXXXXXXXXXX" />
              </div>
            </div> */}
          </form>
        </div>
      </SettingsMainContentWrapper>
      <SettingsActionButtons
        onSave={handlePasswordReset}
        onCancel={handleFormReset}
        canSave={formMethods.formState.isDirty}
        canCancel={formMethods.formState.isDirty || canChangePass}
      />
    </SettingsGridWrapper>
  );
}
